import { useState } from "react";
import webApiClient from "../api/client";
import { parseICS } from "../utils/iCalParser";

/**
 * Custom hook for ICS file import and conflict resolution
 * Extracted from AdminConsole.js to enable reuse across components
 * Maintains exact same naming conventions and error handling
 */
const useICSImport = (options = {}) => {
    const {
        mode = "multi", // "multi" for AdminConsole, "single" for TicketWizard
        filterFacebookEvents = true,
        onEventsImported = null,
        onOwnershipConflict = null // NEW: Callback for ownership conflicts (TicketWizard only)
    } = options;

    // State management - same structure as AdminConsole
    const [events, setEvents] = useState([]);
    const [isUploading, setIsUploading] = useState(false);
    const [uploadStatus, setUploadStatus] = useState("");
    const [pendingConflicts, setPendingConflicts] = useState([]);
    const [showConflictModal, setShowConflictModal] = useState(false);
    const [resolvedConflicts, setResolvedConflicts] = useState([]);

    // Detect dance styles based on event title and description
    // Enhanced version with comprehensive keywords (preserving AdminConsole fallback for now)
    const detectDanceStyles = (title = "", description = "") => {
        const content = (title + " " + description).toLowerCase();
        const styles = [];

        // Enhanced detection with more keywords
        if (content.includes("salsa")) styles.push("salsa");
        if (content.includes("bachata")) styles.push("bachata");
        if (content.includes("zouk") || content.includes("brazilian zouk")) styles.push("zouk");
        if (content.includes("kizomba")) styles.push("kizomba");
        if (content.includes("tango") || content.includes("argentine tango")) styles.push("tango");
        if (content.includes("west coast swing") || content.includes("wcs") || content.includes("west coast")) styles.push("west coast swing");
        if (content.includes("forro") || content.includes("forró")) styles.push("forro");
        if (content.includes("lambada")) styles.push("lambada");
        if (content.includes("samba") || content.includes("gafieira")) styles.push("samba (gaf)");
        if (content.includes("bolero")) styles.push("bolero");
        if (content.includes("merengue")) styles.push("merengue");
        if (content.includes("mambo")) styles.push("mambo");
        if (content.includes("reggaeton") || content.includes("perreo")) styles.push("reggaeton");
        if (content.includes("hip-hop") || content.includes("hiphop") || content.includes("hip hop")) styles.push("hip-hop");

        // Default to 'other' if no styles detected
        if (styles.length === 0) styles.push("other");

        return styles;
    };

    // Merge new events with existing events, avoiding duplicates
    // Exact copy from AdminConsole
    const mergeEvents = (existingEvents, newEvents) => {
        const eventMap = new Map();

        // Add existing events to map
        existingEvents.forEach(event => {
            eventMap.set(event.id, event);
        });

        // Add or update with new events
        newEvents.forEach(event => {
            eventMap.set(event.id, event);
        });

        return Array.from(eventMap.values());
    };

    // Determine conflict type - exact copy from AdminConsole
    const determineConflictType = (newEvent, existingEvent) => {
        // Same Facebook event from different import sources
        if (newEvent.facebookUrl && existingEvent.facebookEventId) {
            const newFbId = newEvent.facebookUrl.split("/events/")[1]?.split("/")[0];
            if (existingEvent.facebookEventId === newFbId) {
                return "facebook_duplicate";
            }
        }

        // Same iCal UID (updated event)
        if (newEvent.icalUID && existingEvent.icalUID === newEvent.icalUID) {
            return "ical_update";
        }

        // Potential duplicate based on title/date
        return "potential_duplicate";
    };

    // Process the iCalendar file - exact logic from AdminConsole with mode support
    const processICalFile = async (content) => {
        console.log("Processing iCal file, length:", content.length);

        setIsUploading(true);
        setUploadStatus("Processing iCalendar file...");

        try {
            // Parse iCalendar data using our custom parser
            const parsedEvents = parseICS(content);

            // Filter to only Facebook events if enabled
            let filteredEvents = parsedEvents;
            if (filterFacebookEvents) {
                filteredEvents = parsedEvents.filter(
                    event => event.url && event.url.includes("facebook.com/events/")
                );

                if (filteredEvents.length === 0) {
                    setUploadStatus("No Facebook events found. Please upload a Facebook iCalendar file.");
                    return null;
                }
            }

            // Transform events to our app format
            const formattedEvents = filteredEvents.map(event => {
                const extractedFbId = event.url?.split("/events/")[1]?.split("/")[0];

                return {
                    id: crypto.randomUUID(), // Temporary ID for matching images
                    icalUID: event.id, // Store separately for duplicate detection
                    title: event.summary || "Title not parsed in processICalFile",
                    description: event.description || "",
                    // Return plain ISO strings - form fields and backend both expect this format
                    eventStartDateTime: event.start.toISOString(),
                    eventEndDateTime: (event.end || new Date(event.start.getTime() + 2 * 60 * 60 * 1000)).toISOString(),
                    eventLocation: {
                        description: event.location || "Location TBA",
                        latitude: null,
                        longitude: null,
                    },
                    danceStyles: detectDanceStyles(event.summary, event.description),
                    isEventOwner: false,
                    facebookUrl: event.url,
                    facebookEventId: extractedFbId,
                    importSource: "ical",
                };
            });

            // For single mode (TicketWizard), only take the first event
            const eventsToProcess = mode === "single" ? [formattedEvents[0]] : formattedEvents;

            // Check if the event is in the past
            const today = new Date();
            today.setHours(0, 0, 0, 0);

            const eventDate = new Date(eventsToProcess[0].eventStartDateTime);
            eventDate.setHours(0, 0, 0, 0);

            if (eventDate < today) {
                setUploadStatus(
                    `Error: This event is in the past (${eventDate.toLocaleDateString()}). Please check your file.`
                );
                return null;
            }

            // Check if events already exist in frontend table
            const alreadyInTable = eventsToProcess.filter(event =>
                events.some(
                    existingEvent =>
                        existingEvent.icalUID === event.icalUID ||
                        (existingEvent.facebookEventId && existingEvent.facebookEventId === event.facebookEventId)
                )
            );

            if (alreadyInTable.length > 0) {
                const duplicateNames = alreadyInTable.map(e => e.title).join(", ");
                setUploadStatus(
                    `Event(s) already in table: ${duplicateNames}. This file contains events you've already loaded.`
                );
                return null;
            }

            // Create lightweight check data for backend duplicate detection
            const eventChecks = eventsToProcess.map(event => {
                const facebookId = event.facebookUrl ? event.facebookUrl.split("/events/")[1]?.split("/")[0] : null;

                return {
                    title: event.title,
                    date: event.eventStartDateTime,
                    facebookId: facebookId,
                    icalUID: event.icalUID,
                };
            });

            console.log("Checking", eventChecks.length, "events for duplicates");

            // Check for duplicates with backend (V2 API)
            // Pass currentUserOrgId for ownership comparison (TicketWizard only)
            const payload = { eventChecks };
            if (options.currentUserOrgId) {
                payload.currentUserOrgId = options.currentUserOrgId;
            }

            const response = await webApiClient.post("/api/v2/event/check-duplicates", payload);
            const { duplicateResults } = await response.data;

            // Process results - handle ownership conflicts for TicketWizard
            const conflicts = [];
            const uniqueEvents = [];
            let ownershipConflict = null;

            eventsToProcess.forEach((event, index) => {
                const result = duplicateResults[index];
                if (result.isDuplicate) {
                    // Check if this is an ownership conflict (for TicketWizard mode)
                    if (mode === "single" && result.ownershipInfo && onOwnershipConflict) {
                        // Backend provides action guidance: EDIT_EXISTING, CLAIM_OWNERSHIP, or BLOCK
                        const action = result.ownershipInfo.action;

                        if (action === "EDIT_EXISTING") {
                            // YOU already own this - show edit dialog via ownership conflict callback
                            ownershipConflict = {
                                newEvent: event,
                                existingEvent: result.existingEvent,
                                ownershipInfo: result.ownershipInfo,
                            };
                        } else if (action === "CLAIM_OWNERSHIP") {
                            // Event exists without tickets - show ownership claim modal
                            ownershipConflict = {
                                newEvent: event,
                                existingEvent: result.existingEvent,
                                ownershipInfo: result.ownershipInfo,
                            };
                        } else if (action === "BLOCK") {
                            // Someone else owns this - block with error
                            ownershipConflict = {
                                newEvent: event,
                                existingEvent: result.existingEvent,
                                ownershipInfo: result.ownershipInfo,
                            };
                        } else {
                            // No action provided (legacy compatibility) - show ownership modal
                            ownershipConflict = {
                                newEvent: event,
                                existingEvent: result.existingEvent,
                                ownershipInfo: result.ownershipInfo,
                            };
                        }
                    } else {
                        // Regular event conflict (for AdminConsole mode)
                        conflicts.push({
                            newEvent: event,
                            existingEvent: result.existingEvent,
                            conflictType: determineConflictType(event, result.existingEvent),
                            lastUpdated:
                                result.existingEvent?.lastModified ||
                                result.existingEvent?.updatedAt ||
                                result.existingEvent?.importHistory?.[0]?.importDate ||
                                result.existingEvent?.createdAt ||
                                "Unknown",
                            existingSource: result.existingEvent?.importSource || "unknown",
                        });
                    }
                } else {
                    uniqueEvents.push(event);
                }
            });

            // Priority 1: Handle ownership conflicts (TicketWizard)
            if (ownershipConflict && onOwnershipConflict) {
                console.log("🔒 Ownership conflict detected, triggering ownership modal");
                onOwnershipConflict(ownershipConflict);
                setUploadStatus("Event already exists. Please confirm ownership.");
                return { ownershipConflict, uniqueEvents };
            }

            // Priority 2: Handle event conflicts (AdminConsole)
            if (conflicts.length > 0) {
                setPendingConflicts(conflicts);
                setShowConflictModal(true);
                setUploadStatus(`Found ${conflicts.length} potential conflicts. Please review.`);
                return { conflicts, uniqueEvents };
            }

            // Priority 3: No conflicts - add unique events
            const mergedEvents = mergeEvents(events, uniqueEvents);
            setEvents(mergedEvents);
            setUploadStatus(`Added ${uniqueEvents.length} new events.`);

            // Send events back to parent component
            if (onEventsImported) {
                onEventsImported(mergedEvents);
            }

            return { events: uniqueEvents, conflicts: [] };

        } catch (error) {
            console.error("Error processing iCalendar file:", error);
            setUploadStatus(`Error: ${error.message}`);
            return null;
        } finally {
            setIsUploading(false);
        }
    };

    // Handle conflict resolutions - exact copy from AdminConsole
    const handleConflictResolutions = (resolutions) => {
        // Store the resolutions for later use
        setResolvedConflicts(resolutions);

        const eventsToAdd = resolutions
            .filter(r => r.action === "overwrite" || r.action === "update")
            .map(r => r.newEvent);

        const mergedEvents = mergeEvents(events, eventsToAdd);
        setEvents(mergedEvents);

        setShowConflictModal(false);
        setPendingConflicts([]);
        setUploadStatus(`Processed ${eventsToAdd.length} conflict resolutions.`);

        // Send events back to parent component
        if (onEventsImported) {
            onEventsImported(mergedEvents);
        }

        return eventsToAdd;
    };

    // Clear all data
    const clearEvents = () => {
        setEvents([]);
        setPendingConflicts([]);
        setResolvedConflicts([]);
        setUploadStatus("");
        setShowConflictModal(false);
    };

    return {
        // State
        events,
        isUploading,
        uploadStatus,
        pendingConflicts,
        showConflictModal,
        resolvedConflicts,
        
        // Actions
        processICalFile,
        handleConflictResolutions,
        clearEvents,
        
        // Utilities
        detectDanceStyles,
        mergeEvents,
        determineConflictType,
        
        // State setters for manual control if needed
        setEvents,
        setUploadStatus,
        setShowConflictModal
    };
};

export default useICSImport;